import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import L, { type LatLng } from "leaflet";
import { readableDistance } from "@/features/map/utils/readableSize";

/**
 * 円描画中に半径をメートル単位でリアルタイム表示するコンポーネント。
 * leaflet-draw の組み込みツールチップは 1km 未満の値を表示しない問題があるため
 * mousedown/mousemove イベントで独自計算した距離を Portal で描画する。
 */
export const CircleRadiusDisplay: React.FC = () => {
  const map = useMap();
  const [radius, setRadius] = useState<number | null>(null);
  const centerRef = useRef<LatLng | null>(null);
  const isDrawingCircleRef = useRef(false);

  useEffect(() => {
    const onDrawStart = (e: L.LeafletEvent): void => {
      if (
        (e as L.LeafletEvent & { layerType: string }).layerType === "circle"
      ) {
        isDrawingCircleRef.current = true;
      }
    };
    const onDrawStop = (): void => {
      isDrawingCircleRef.current = false;
      centerRef.current = null;
      setRadius(null);
    };
    const onMouseDown = (e: L.LeafletMouseEvent): void => {
      if (isDrawingCircleRef.current) {
        centerRef.current = e.latlng;
      }
    };
    const onMouseMove = (e: L.LeafletMouseEvent): void => {
      if (isDrawingCircleRef.current && centerRef.current) {
        setRadius(map.distance(centerRef.current, e.latlng));
      }
    };

    map.on(L.Draw.Event.DRAWSTART, onDrawStart);
    map.on(L.Draw.Event.DRAWSTOP, onDrawStop);
    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);

    return () => {
      map.off(L.Draw.Event.DRAWSTART, onDrawStart);
      map.off(L.Draw.Event.DRAWSTOP, onDrawStop);
      map.off("mousedown", onMouseDown);
      map.off("mousemove", onMouseMove);
    };
  }, [map]);

  if (radius === null) return null;

  const radiusText = readableDistance(radius);

  return createPortal(
    <div
      style={{
        position: "absolute",
        bottom: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        background: "rgba(0, 0, 0, 0.7)",
        color: "white",
        padding: "4px 12px",
        borderRadius: "4px",
        zIndex: 1000,
        fontSize: "14px",
        fontWeight: "bold",
        pointerEvents: "none",
        whiteSpace: "nowrap",
      }}
    >
      半径: {radiusText}
    </div>,
    map.getContainer()
  );
};
